/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogConfig } from '@angular/material/dialog';
import { GridViews, WcgButtonComponent } from '@wcg/frontend-framework';
import {
  GRID_FACADE_SERVICE,
  GRID_LABELS,
  GridView,
  GridViewAbstract,
  GridViewDialogData,
  ViewActionType,
} from '@data-access/grid-views';
import { NgClass } from '@angular/common';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, WcgButtonComponent, NgClass, MatMenuModule],
  providers: [],
  selector: 'wcg-create-grid-view-actions',
  styleUrls: ['./create-grid-view.component.scss'],
  template: `
    @if ($shouldShowActions()) {
      <div class="flex gap-4">
        <wcg-button color="tertiary" size="slim" (click)="revertGridSettings()">
          <mat-icon>refresh</mat-icon>Revert Grid Setting</wcg-button
        >
        <wcg-button color="tertiary" size="slim">
          <span
            [ngClass]="{
              'create-grid-view__disable-btn': isDisabled(),
              'create-grid-view__not-default-view':
                $selectedViewOption().label !== 'Default View',
            }"
            (click)="openSaveDialog(viewActionLabels.save, $event)"
            >{{ label() }}</span
          >
          @if ($selectedViewOption().label !== 'Default View') {
            <span
              [matMenuTriggerFor]="updateView"
              (click)="$event.stopPropagation(); matIconUp.set(!matIconUp())"
              class="relative top-1"
            >
              @if (!matIconUp()) {
                <mat-icon class="grid-view-drop-down__icon"
                  >keyboard_arrow_down</mat-icon
                >
              }
              @if (matIconUp()) {
                <mat-icon class="grid-view-drop-down__icon"
                  >keyboard_arrow_up</mat-icon
                >
              }
            </span>
          }
        </wcg-button>
      </div>
    }
    <mat-menu
      #updateView="matMenu"
      yPosition="below"
      (closed)="matIconUp.set(!matIconUp())"
    >
      <!-- @Todo -->
      <button
        mat-menu-item
        (click)="openSaveDialog(viewActionLabels.createNewView, $event)"
        [ngClass]="{ 'create-grid-view__disable-btn': isDisabled() }"
      >
        Create new view
      </button>
      @if (
        $selectedViewOption().label.toLowerCase() !== 'default view' &&
        $currentGridView() &&
        $shouldShowActions()
      ) {
        <button
          mat-menu-item
          (click)="openSaveDialog(viewActionLabels.updateExistingView, $event)"
        >
          Update existing view
        </button>
      }
    </mat-menu>
  `,
})
export class CreateGridViewActionsComponent {
  private readonly _gvFacadeService =
    inject<GridViewAbstract>(GRID_FACADE_SERVICE);

  private readonly _matMenuTrigger = viewChild(MatMenuTrigger);
  matIconUp = signal<boolean>(false);

  viewActionLabels = GRID_LABELS;

  $selectedViewOption = this._gvFacadeService.$selectedView;
  $currentGridView = this._gvFacadeService.$currentGridView;
  $gridType = this._gvFacadeService.$gridType;
  $isTableColumnChanged = this._gvFacadeService.$isTableColumnChanged;

  /** Show Revert / Save only when advanced filters differ from the selected view's saved config */
  $shouldShowActions = computed(
    () =>
      this._gvFacadeService.$hasFilterChanges() ||
      this._gvFacadeService.$isTableColumnChanged(),
  );

  label = input<GRID_LABELS>(GRID_LABELS.save);
  data = input<GridViews>();
  isDisabled = computed(() => this._gvFacadeService.$gridTotal() >= 5);

  revertGridState = output<void>();

  openSaveDialog(viewAction: string, $event: Event): void {
    $event.stopPropagation();
    this._matMenuTrigger()?.closeMenu();
    const config: MatDialogConfig = {
      autoFocus: false,
      width: '550px',
    };

    const _constructGridDialogData = (props: {
      header: string;
      buttonLabel: string;
      viewAction: ViewActionType;
    }): GridViewDialogData<GridViews> => ({
      config: config,
      data: this.data() as GridViews,
      gridBtnLabel: props.buttonLabel,
      gridHeader: props.header,
      gridProps: getGridProps(),
      gridViewAbstract: this._gvFacadeService,
      viewAction: props.viewAction,
    });
    const getViewAction = (): void => {
      switch (viewAction) {
        case GRID_LABELS.createNewView:
          this._gvFacadeService.openGridDialog(
            _constructGridDialogData({
              buttonLabel: GRID_LABELS.createNewView,
              header: GRID_LABELS.createNewView,
              viewAction: GRID_LABELS.create,
            }),
          );
          break;
        case GRID_LABELS.updateExistingView:
          this._gvFacadeService.openGridDialog(
            _constructGridDialogData({
              buttonLabel: GRID_LABELS.updateView,
              header: GRID_LABELS.updateExistingView,
              viewAction: GRID_LABELS.update,
            }),
          );
          break;
        default:
          this._gvFacadeService.openGridDialog(
            _constructGridDialogData({
              buttonLabel: GRID_LABELS.saveView,
              header: GRID_LABELS.saveView,
              viewAction: GRID_LABELS.save,
            }),
          );
          break;
      }
    };
    const getGridProps = () => {
      const gridProps = {
        description: '',
        grid_type: this.$gridType(),
      };
      if (viewAction === GRID_LABELS.updateExistingView) {
        return {
          ...gridProps,
          id: this.$currentGridView().id,
          name: this.$currentGridView().name,
        } as Omit<GridView<unknown>, 'data'> | undefined;
      }
      return gridProps as Omit<GridView<unknown>, 'data'> | undefined;
    };
    getViewAction();
  }
  revertGridSettings(): void {
    this.revertGridState.emit();
    this._gvFacadeService.revertGridSettings();
  }
}
